import { useId, useState, type DragEvent } from 'react';
import { FileUp } from 'lucide-react';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle, Spinner, cn } from '@design-system';

interface DocumentDropzoneProps {
  accept: string;
  isUploading: boolean;
  onFiles: (files: File[]) => void;
}

/** A dashed area to drop documents on, or click to choose them; it shows progress while uploading. */
export function DocumentDropzone({ accept, isUploading, onFiles }: DocumentDropzoneProps) {
  const inputId = useId();
  const [isDragging, setIsDragging] = useState(false);

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setIsDragging(false);
    if (!isUploading) onFiles(Array.from(event.dataTransfer.files));
  }

  return (
    <label
      htmlFor={inputId}
      data-testid="consumer-document-dropzone"
      aria-busy={isUploading}
      onDragOver={event => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={cn(
        'block cursor-pointer rounded-xl border-2 border-dashed bg-white transition-colors duration-150 ease-out',
        'hover:border-primary/60 hover:bg-primary/5 focus-within:ring-primary/40 focus-within:ring-2',
        isDragging && 'border-primary bg-primary/5',
        isUploading && 'pointer-events-none opacity-80',
      )}
    >
      <Empty className="p-6 md:p-10">
        <EmptyHeader>
          <EmptyMedia variant="icon" className="bg-primary/10 text-primary">
            {isUploading ? <Spinner /> : <FileUp />}
          </EmptyMedia>
          <EmptyTitle>{isUploading ? 'Uploading your documents…' : 'Drag and drop your documents here'}</EmptyTitle>
          <EmptyDescription>
            {isUploading ? 'This can take a moment for large files.' : 'or click to choose files. PDF, JPG or PNG.'}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
      <input
        id={inputId}
        type="file"
        multiple
        accept={accept}
        className="sr-only"
        aria-label="Choose documents"
        disabled={isUploading}
        onChange={event => {
          onFiles(Array.from(event.target.files ?? []));
          event.target.value = '';
        }}
      />
    </label>
  );
}
